import styled from "styled-components";
import { Button, Text } from "@lmn/design-system";
import media from "@/styles/mediaQueries";

export const Wrapper = styled.div`
  margin-bottom: 48px;
  border: 1px solid
    ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
  border-radius: 8px;
`;

export const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 16px;
  text-align: left;
  & + svg {
    margin-right: 16px;
    min-width: 16px;
  }
`;

export const Content = styled.div`
  padding: 16px;
  border-top: 1px solid
    ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
`;

export const CalendarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  contain: inline-size;
`;

export const BarsWrapper = styled.div`
  display: flex;
  gap: 4px;
  overflow-x: auto;
  flex: 1;
  min-width: 0;
  padding: 50px 0 8px 0;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const NavButton = styled.button<{ $disabled?: boolean }>(
  ({ theme, $disabled }) => ({
    display: "none",
    alignItems: "center",
    justifyContent: "center",
    width: "40px",
    height: "40px",
    minWidth: "40px",
    minHeight: "40px",
    borderRadius: theme.kallisto.border.radius.radiusFull,
    border: `1px solid ${theme.kallisto.color.border.static.borderNeutral}`,
    backgroundColor: theme.kallisto.color.common.white[100],
    cursor: $disabled ? "not-allowed" : "pointer",
    opacity: $disabled ? 0.4 : 1,
    transition: "background-color 0.1s ease",

    "&:hover": {
      backgroundColor: $disabled
        ? theme.kallisto.color.common.white[100]
        : theme.kallisto.color.background.interactive.backgroundNeutralHover,
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.kallisto.color.common.white[1000]}`,
      outlineOffset: 0,
    },
    "&:active": {
      backgroundColor: $disabled
        ? theme.kallisto.color.common.white[100]
        : theme.kallisto.color.background.interactive.backgroundNeutralPressed,
    },

    svg: {
      width: "16px",
      height: "16px",
      fill: theme.kallisto.color.icon.static.iconPrimary,
    },

    ...media.screenSmMin({
      display: "flex",
    }),
  }),
);

export const BarColumn = styled.button<{ $isUnavailable?: boolean }>(
  ({ theme, $isUnavailable }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    cursor: $isUnavailable ? "default" : "pointer",
    minWidth: "70px",
    position: "relative",
    background: "none",
    border: "none",
    padding: 0,
    font: "inherit",

    "&:focus-visible": {
      outline: `2px solid ${theme.kallisto.color.common.black[1000]}`,
      outlineOffset: "2px",
      borderRadius: "4px",
    },
  }),
);

export const BarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 120px;
  position: relative;
`;

export const BarInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const Bar = styled.div<{
  $height: number;
  $isCheapest?: boolean;
  $isSelected?: boolean;
}>(({ theme, $height, $isCheapest, $isSelected }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "space-between",
  width: "70px",
  height: `${$height}px`,
  padding: "8px 0",
  backgroundColor: $isSelected
    ? theme.kallisto.color.background.interactive.backgroundPrimaryDefault
    : $isCheapest
      ? theme.kallisto.color.common.semantic.success[200]
      : theme.kallisto.color.background.static.backgroundDisabled,
  borderRadius: "4px 4px 0 0",
  transition: "background-color 0.15s ease",

  [`${BarColumn}:hover &`]: {
    backgroundColor: $isSelected
      ? theme.kallisto.color.background.interactive.backgroundPrimaryPressed
      : theme.kallisto.color.background.interactive.backgroundPrimaryHover,
  },
}));

export const AirportCode = styled.span<{ $isSelected?: boolean }>(
  ({ theme, $isSelected }) => ({
    fontSize: theme.kallisto.font.size[150],
    fontWeight: theme.kallisto.font.weight.regular,
    color: $isSelected
      ? theme.kallisto.color.text.static.textOnDark
      : theme.kallisto.color.text.static.textSecondary,
  }),
);

export const PriceLabel = styled(Text)<{ $isSelected?: boolean }>(
  ({ theme, $isSelected }) => ({
    fontWeight: theme.kallisto.font.weight.bold,
    color: $isSelected
      ? theme.kallisto.color.text.static.textOnDark
      : theme.kallisto.color.text.static.textPrimary,
  }),
);

export const DateLabel = styled.div<{ $isWeekend?: boolean }>(
  ({ theme, $isWeekend }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginTop: "8px",
    fontSize: theme.kallisto.font.size[150],
    lineHeight: theme.kallisto.font.lineHeight[200],
    color: theme.kallisto.color.text.static.textSecondary,
    fontWeight: $isWeekend ? theme.kallisto.font.weight.bold : "normal",
  }),
);

export const TooltipContent = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

export const TooltipPrice = styled.span(({ theme }) => ({
  color: theme.kallisto.color.common.white[1000],
  fontWeight: theme.kallisto.font.weight.medium,
  fontSize: theme.kallisto.font.size[200],
}));

export const TooltipDetails = styled.span(({ theme }) => ({
  fontSize: theme.kallisto.font.size[150],
  color: theme.kallisto.color.common.white[900],
}));

export const UnavailableBar = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: "70px",
  height: "30px",
  backgroundColor: theme.kallisto.color.background.static.backgroundLayer01,
  borderRadius: "4px 4px 0 0",
  color: theme.kallisto.color.text.static.textSecondary,

  svg: {
    width: "12px",
    height: "12px",
    fill: theme.kallisto.color.icon.static.iconSecondary,
    marginBottom: "0",
  },
}));

export const DismissButton = styled(Button)(() => ({
  marginTop: "16px",
}));
